import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseConfigService } from '@fuse/services/config.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';

import { NavigationEnd, Router } from '@angular/router';
import { locale as navigationEnglish } from 'app/navigation/i18n/en';
import { locale as navigationTurkish } from 'app/navigation/i18n/tr';
import { navigation } from 'app/navigation/navigation';
import { AuthenticationService } from './helpers/authentication.service';
import { PermissionModulesService } from './helpers/permission-modules.service';
import { ConfigVersion } from './shared/config-version.service';
import { EnumFuncoes } from './shared/enum.service';
import { Funcoes } from './shared/funcoes.mode';
import { IndexedDbService } from './shared/indexed-db.service';

@Component({
    selector   : 'app',
    templateUrl: './app.component.html',
    styleUrls  : ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy
{
    fuseConfig: any;
    navigation: any;
    navegacaoProvi: any = [];
    funcoes: Funcoes[];

    private _unsubscribeAll: Subject<any>;
    // private funcoes: Funcoes[];
    /**
     * Constructor
     *
     * @param {DOCUMENT} document
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseNavigationService} _fuseNavigationService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {FuseSplashScreenService} _fuseSplashScreenService
     * @param {FuseTranslationLoaderService} _fuseTranslationLoaderService
     * @param {Platform} _platform
     * @param {TranslateService} _translateService
     */
    constructor(
        @Inject(DOCUMENT) private document: any,
        private _fuseConfigService: FuseConfigService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseSidebarService: FuseSidebarService,
        private _fuseSplashScreenService: FuseSplashScreenService,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        @Optional() private _auths: AuthenticationService,
        private _translateService: TranslateService,
        private _platform: Platform,
        private route: Router,
        private _configVersion: ConfigVersion,
        private indexedDbService: IndexedDbService,
        private _permissionModuleService: PermissionModulesService,
    )
    {
        // Get default navigation
        this.navigation = navigation;
        if (this._auths.currentUserValue){
            this._configVersion.buscaDadosCliente(this._auths.currentUserValue.accountId);
            this.funcoes = this._auths.currentUserValue.userDetails.userRules;
        }

        if (this.funcoes) {
            const isAdminORGestor = this.funcoes.some(f => (f.funcao === EnumFuncoes.Administrador && f.ativo) || (f.funcao === EnumFuncoes.Gestor && f.ativo));

            if (isAdminORGestor) {
                this.navegacaoProvi = [...navigation];
            } else {
                this.navegacaoProvi = [];

                this.funcoes.filter(f => f.ativo).forEach(funcao => {
                    const regra = this._permissionModuleService.funcaoRules[funcao.funcao];

                    if (regra) {
                        navigation.forEach(modulo => {
                            const children = modulo.children || [];

                            const filteredLevel1 = children.map((sub1: any) => {
                                const grandchildren = sub1.children || [];

                                const filteredGrandchildren = grandchildren.filter((sub2: any) => {
                                    try { return regra.allowed(sub2); }
                                    catch (e) { return false; }
                                });

                                const sub1Allowed = (() => {
                                    try { return regra.allowed(sub1); }
                                    catch (e) { return false; }
                                })();

                                if (sub1Allowed || filteredGrandchildren.length > 0) {
                                    return {
                                        ...sub1,
                                        ...(filteredGrandchildren.length > 0 ? { children: filteredGrandchildren } : {})
                                    };
                                }

                                return null;
                            }).filter(x => x !== null);
                            const moduloAllowed = (() => {
                                try { return regra.allowed(modulo); }
                                catch (e) { return false; }
                            })();

                            if (moduloAllowed || filteredLevel1.length > 0) {
                                this.navegacaoProvi.push({
                                    ...modulo,
                                    ...(filteredLevel1.length > 0 ? { children: filteredLevel1 } : {})
                                });
                            }
                        });
                    }
                });
            }
            this.navigation = this.navegacaoProvi;
        }
        
        // Register the navigation to the service
        this._fuseNavigationService.register('main', this.navigation);

        // Set the main navigation as our current navigation
        this._fuseNavigationService.setCurrentNavigation('main');

        // Add languages
        this._translateService.addLangs(['en', 'tr']);

        // Set the default language
        this._translateService.setDefaultLang('en');

        // Set the navigation translations
        this._fuseTranslationLoaderService.loadTranslations(navigationEnglish, navigationTurkish);

        // Use a language
        this._translateService.use('en');

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix Start
         * ----------------------------------------------------------------------------------------------------
         */

        /**
         * If you are using a language other than the default one, i.e. Turkish in this case,
         * you may encounter an issue where some of the components are not actually being
         * translated when your app first initialized.
         *
         * This is related to ngxTranslate module and below there is a temporary fix while we
         * are moving the multi language implementation over to the Angular's core language
         * service.
         **/

        // Set the default language to 'en' and then back to 'tr'.
        // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
        // been selected and there is no way to force it, so we overcome the issue by switching
        // the default language back and forth.
        /**
         setTimeout(() => {
            this._translateService.setDefaultLang('en');
            this._translateService.setDefaultLang('tr');
         });
         */

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix End
         * ----------------------------------------------------------------------------------------------------
         */

        // Add is-mobile class to the body if the platform is mobile
        if ( this._platform.ANDROID || this._platform.IOS )
        {
             this.document.body.classList.add('is-mobile');
        }

        // Set the private defaults
        this._unsubscribeAll = new Subject();

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    async ngOnInit(): Promise<void>
    {
        // Subscribe to config changes
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {

                this.fuseConfig = config;

                // Boxed
                if ( this.fuseConfig.layout.width === 'boxed' )
                {
                    this.document.body.classList.add('boxed');
                }
                else
                {
                    this.document.body.classList.remove('boxed');
                }

                // Color theme - Use normal for loop for IE11 compatibility
                for ( let i = 0; i < this.document.body.classList.length; i++ )
                {
                    const className = this.document.body.classList[i];

                    if ( className.startsWith('theme-') )
                    {
                        this.document.body.classList.remove(className);
                    }
                }

                this.document.body.classList.add(this.fuseConfig.colorTheme);
            });

            this.route.events.subscribe(event => {
                
                if (event instanceof NavigationEnd) {
                  const googleButton = document.getElementById('google-signin-button');
                  
                  if (googleButton) {
                    if (event.url.includes('/a2w/gestor/agenda')) {
                      googleButton.style.display = 'block';
                    } else {
                      googleButton.style.display = 'none';
                    }
                  }
                }
              });

              await this.indexedDbService.initDB(); // 🔥 Inicializa o banco ao iniciar o app
                //console.log('✅ IndexedDB iniciado!');
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void
    {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }
}
